import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { defaultDateTimeFormat, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import dayjs from 'dayjs';
import { ContractConsentAction, useContractConsentProvider } from '../ContractConsent.Hooks';
import ContractConsentsFilters from './ContractConsent.Filters';
import ContractConsentsInfo from './ContractConsent.Info';
import { isDesktop, isMobile } from 'react-device-detect';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import Box from 'assets/components/box/Box';

export default function ContractConsentList() {
  const { lang } = getLocales();
  const { permissions } = useAuthStore();
  const { listApi, pageRouter, getAcceptedStatus, getTokenStatus } = useContractConsentProvider();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.consents}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      isLoading={listApi.isExecuting}
      sidebar={<ContractConsentsInfo />}
      header={
        <>
          <h2 style={{ marginRight: 'auto' }} children={lang.consents} />
          {isMobile && <ContractConsentsFilters />}
        </>
      }
      subHeader={isDesktop && <ContractConsentsFilters />}
      render={
        <table className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>{lang.consentNumber}</th>
              <th>{lang.customerName}</th>
              <th>{lang.lastModified}</th>
              <th>{lang.product}</th>
              <th>{lang.dealer}</th>
              <th>{lang.appNumber}</th>
              <th>{lang.vin}</th>
              <th>{lang.accepted}</th>
              <th>{lang.token}</th>
              <th data-actions>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: consent }) => {
                const isResendBlocked = getAcceptedStatus(consent) !== 'N/A' || !permissions.RESEND_CONTRACTCONSENT;
                const isManualAcceptBlocked =
                  getAcceptedStatus(consent) !== 'N/A' || !permissions.MANUALLY_ACCEPT_CONTRACTCONSENT;
                return (
                  <tr key={consent.id}>
                    <td>
                      <b>{consent.id}</b>
                    </td>
                    <td>{consent.customerName}</td>
                    <td>{dayjs(consent?.modifiedAtUtc, defaultServerDateTimeFormat).format(defaultDateTimeFormat)}</td>
                    <td>{consent?.product?.name ? consent?.product?.name : 'N/A'}</td>
                    <td>{consent?.dealer?.name ? consent?.dealer?.name : 'N/A'}</td>
                    <td>{consent?.contract?.appNumber ? consent.contract.appNumber : 'N/A'}</td>
                    <td>{consent?.vehicleVin ? consent?.vehicleVin : 'N/A'}</td>
                    <td>{getAcceptedStatus(consent)}</td>
                    <td>{getTokenStatus(consent)}</td>
                    <td>
                      <Box class={ThemeWrapperStack}>
                        <Button
                          class={ThemeButtonCircle}
                          media={<Icon class="fas-info-circle" />}
                          onClick={pageRouter.updateParams}
                          data={{ id: consent.id.toString(), action: ContractConsentAction.INFO }}
                          htmlElementProps={{ title: lang.info }}
                          active
                        />
                        <Button
                          class={ThemeButtonCircle}
                          media={<Icon class="fas-envelope" />}
                          onClick={pageRouter.updateParams}
                          data={{ id: consent.id.toString(), action: ContractConsentAction.RESEND_CONSENT }}
                          disabled={isResendBlocked}
                          active
                          htmlElementProps={{ title: lang.resendConsent }}
                        />
                        <Button
                          class={ThemeButtonCircle}
                          media={<Icon class="fas-check" />}
                          onClick={pageRouter.updateParams}
                          data={{ id: consent.id.toString(), action: ContractConsentAction.MANUALLY_ACCEPT_CONSENT }}
                          disabled={isManualAcceptBlocked}
                          active
                          htmlElementProps={{ title: lang.manuallyAcceptConsent }}
                        />
                      </Box>
                    </td>
                  </tr>
                );
              }}
            />
          </tbody>
        </table>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) => listApi.execute((body) => ({ ...body, limit, offset: 0 }))}
          onOffsetChange={(offset) => listApi.execute((body) => ({ ...body, offset }))}
        />
      }
    />
  );
}
